class URLRequest {
	public constructor(url:uri.URI, method:string = URLLoadingMethod.GET, data:string = null, requestHeaders:Array<URLRequestHeader> = null) {
		this.url = url;
		this.method = method;
		this.data = data;
		this.requestHeaders = requestHeaders;
	}

	public data:string | FormData;
	public method:string;
	public requestHeaders:Array<URLRequestHeader>;
	public url:uri.URI;
	public responseType:XMLHttpRequestResponseType = '';
}

namespace URLLoadingMethod {
	export const GET:string = 'GET';
	export const POST:string = 'POST';
}

class URLRequestHeader {
	public constructor(name:string, value:string) {
		this.name = name;
		this.value = value;
	}

	public name:string;
	public value:string;

}

declare var XDomainRequest:typeof XMLHttpRequest;
class URLLoader {
	constructor(request:URLRequest = null, activityTimeout:number = 30000) {
		this.assignRequest(request);
		this.resetProps();
		this.activityTimeout = activityTimeout;
	}

	protected _xhttp:XMLHttpRequest;
	public request:URLRequest;
	public activityTimeout:number = 0;
	public activityTimeoutHandle:any = 0;

	public onHTTPStatus = new Signal<(loader:URLLoader, HTTPStatus:number)=>void>();
	public onProgress = new Signal<(loader:URLLoader, progress:number)=>void>();
	public onComplete = new Signal<(loader:URLLoader, responce:any, wasError:boolean)=>void>();
	public onError = new Signal<(loader:URLLoader, responce:any, wasError:boolean)=>void>();


	private _HTTPStatus:number;
	public getHTTPStatus():number {
		return this._HTTPStatus;
	}

	private _responce:any;
	public getResponce():any {
		return this._responce;
	}

	private _completed:boolean;
	public getCompleted() : boolean {
		return this._completed;
	}

	private _loading:boolean;
	public getLoading() : boolean {
		return this._loading;
	}

	private _progress:number;
	/**
		Прогресс загрузки от 0 до 1. При диспатчинге onComplete всегда строго равен 1
	*/
	public getProgress() : number {
		return this._progress;
	}

	private _error:boolean;
	public getError() : boolean {
		return this._error;
	}

	private resetProps():void {
		if (this._loading) {
			throw new URLLoaderError('Calling resetProps while loading');
		}
		this._progress = 0;
		this._HTTPStatus = 0;
		this._responce = null;
		this._completed = false;
		this._loading = false;
		this._error = false;
		this.clearActivityTimeout();
	}

	public updateActivityTimeout():void {
		if (!this.activityTimeout) {
			return;
		}
		this.clearActivityTimeout();
		this.activityTimeoutHandle = setTimeout(this.handler_activityTimeout, this.activityTimeout);
	}

	private clearActivityTimeout():void {
		if (this.activityTimeoutHandle) {
			clearTimeout(this.activityTimeoutHandle);
			this.activityTimeoutHandle = 0;
		}
	}

	public load(req:URLRequest = null):void {
		if (this._loading) {
			throw new URLLoaderError('Calling load while loading');
		}
		this.resetProps();
		this.assignRequest(req);
		let request = this.request;
		if (!request) {
			throw new URLLoaderError('No request while load called');
		}
		//PandaConsole.logDebug('URLLoader::load', request.url.href());
		let xhttp:XMLHttpRequest = this.createXhttp();
		
		this._xhttp = xhttp;
		
		this._loading = true;
		xhttp.open(request.method, request.url.href(), true);
		xhttp.responseType = request.responseType;
		this.addXhttpListeners(xhttp);
		if (this.requestHeadersSupported() && request.requestHeaders) {
			for (let header of request.requestHeaders) {
				xhttp.setRequestHeader(header.name, header.value);
			}
		}
		xhttp.send(request.data);
		this.updateActivityTimeout();
	}

	public destroy():void {
		throw new NotImplementedError('URLLoader::destroy');
	}

	public requestHeadersSupported():boolean {
		return !BrowserSpecific.XDomainRequestNeeded();
	}

	private createXhttp():XMLHttpRequest {
		if (BrowserSpecific.XDomainRequestNeeded()) {
			return new XDomainRequest();
		} else {
			return new XMLHttpRequest();
		}
	}

	private complete(error:boolean):void {
		this._error = error;
		if (this._HTTPStatus !== 200) {
			this._error = true;
			PandaConsole.logWarn('URLLoader::complete wrong status', this._HTTPStatus, this.request.url.href());
		}
		//PandaConsole.logDebug('URLLoader::complete', this.request.url.href());
		let xhttp:XMLHttpRequest = this._xhttp;
		this.removeXhttpListeners(xhttp);
		this._progress = 1;
		this._responce = xhttp.response;
		this._completed = true;
		this._loading = false;
		this.clearActivityTimeout();
		this.onComplete.emit(this, this._responce, this._error);
	}

	private addXhttpListeners(xhttp:XMLHttpRequest):void {
		xhttp.onprogress = this.handler_progress;
		xhttp.onreadystatechange = this.handler_readyStateChange;
		xhttp.onerror = this.handler_error;
	}

	private removeXhttpListeners(xhttp:XMLHttpRequest):void {
		xhttp.onprogress = null;
		xhttp.onreadystatechange = null;
		xhttp.onerror = null;
	}

	private assignRequest(request:URLRequest = null):void {
		let newRequest:URLRequest = null;
		if (request instanceof URLRequest) {
			newRequest = request;
		} else if (request === null) {
			newRequest = this.request;
		} else {
			throw new URLLoaderError('Wrong URLRequest parameter');
		}
		this.request = newRequest;
	}

	private handler_error = (event:ErrorEvent) => {
		PandaConsole.logWarn('URLLoader_handler_error');
		this.complete(true);
	};

	private handler_activityTimeout = ()=> {
		PandaConsole.logWarn('URLLoader_handler_error activity_timeout');
		this.complete(true);
	};

	private handler_progress = (event:ProgressEvent) => {
		// нет никаких гарантий что event.loaded отображает реальный размер.
		// Например при transfer-encoding: chunked длину предсказать не возможно,
		// при этом во всех браузерах указана event.total. и в конце загрузки она может не совпадать с responce.length
		// в Firefox в этом случае размер зипнутых байтов, в хроме анзипнутых.
		if (event.lengthComputable) {
			this._progress = event.loaded / event.total;
		}
		this.updateActivityTimeout();
		this.onProgress.emit(this, this._progress);
		//PandaConsole.logInfo('event.lengthComputable', event.lengthComputable);
	};

	private handler_readyStateChange = () => {
		// это метод должен быть максимально лёгким, его будут вызывать много раз
		let xhttp:XMLHttpRequest = this._xhttp;
		let readyState:number = xhttp.readyState;
		if (readyState === 2) {
			this._HTTPStatus = xhttp.status;
			this.updateActivityTimeout();
			this.onHTTPStatus.emit(this, this._HTTPStatus);
		} else if (readyState === 4) {
			this.updateActivityTimeout();
			this.complete(false);
		}
	}
}

class URLVariables {
	public data:{[key:string]:string};

	constructor(data:{[key:string]:string} = {}) {
		this.data = data;
	}

	public toString():string {
		let arr = new Array<string>();
		for (let key in this.data) {
			arr.push(encodeURIComponent(key) + '=' + encodeURIComponent(this.data[key]));
		}
		return arr.join('&').replace(/%20/g, '+');
	}


}

class URLLoaderError extends PandaError {
}

